import { useCallback, useMemo } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import { useSelector } from 'react-redux';
import { ProjectAPI } from 'api/project';
import { FieldDataType } from 'components/organisms/module-settings/fields/types';
import { FieldType, FilterableField } from 'components/organisms/tasks/fields';
import { TemplateObjectType } from 'data/templates/api';
import { getActiveTemplatePlaceholders } from 'data/templates/projects/selectors';
import { useGetAllTeamsFromIndexedDbConditionally } from 'hooks/use-idb';
import {
  useEnabledProjectFieldsQuery,
  useTeamMemberFieldsQuery,
} from 'queries';
import { pascalCase } from 'utils/pascal-case';
import { useProjectCustomFieldsAsFilterableFields } from 'utils/project-fields/use-project-custom-fields-as-filterable-fields';
import { useProjectNativeFieldsForVariables } from 'utils/project-fields/use-project-native-fields-as-filterable-fields';

const getProjectVariablesKey = (projectId?: number): string =>
  `project_${projectId}_variables`;

type ProjectVariables = {
  [key: string]: any;
};

const nativeMemberFieldsForMultiUser = [
  'user_id',
  'full_name',
  'first_name',
  'last_name',
  'email_id',
];

/**
 * Ideally should be retrieved from BE alike project and task fields.
 * hardcoding in FE for now for listing along with custom member fields
 * for project field of type USER in simple variables popover.
 */
export const useNativeMemberFieldsOptions = () => {
  const fields = [...nativeMemberFieldsForMultiUser, 'role'];

  return fields.map((_field) => {
    const phrases = _field.split('_');
    const capitalised = phrases.map((_phrase) => pascalCase(_phrase));

    return {
      id: _field,
      label: capitalised.join(' '),
      value: `${phrases.shift()}${pascalCase(phrases.join(''))}`,
    };
  });
};

export const useCalculatedProjectFields = () => {
  const fields = [
    'current_phases',
    'completion_percentage',
    'actual_start_date',
    'actual_completed_date',
    'project_tracked_hours',
    'total_billable_hours',
    'overall_rating',
    'last_milestone_rating',
    'project_age',
    'planned_duration',
    'completed_tasks',
    'overdue_tasks',
    'blocked_tasks',
    'atRisk_tasks',
    'approval_pending',
  ];

  return fields.map((_field) => {
    const phrases = _field.split('_');
    const capitalised = phrases.map((_phrase) => pascalCase(_phrase));

    return {
      fieldId: _field,
      fieldLabel: capitalised.join(' '),
      fieldName: `${phrases.shift()}${phrases.map(pascalCase).join('')}`,
    };
  });
};

export const useProjectVariablesQuery = (
  projectId?: number,
  queryOptions: UseQueryOptions<
    ProjectVariables,
    unknown,
    ProjectVariables,
    string
  > = {},
): UseQueryResult<ProjectVariables> =>
  useQuery(
    getProjectVariablesKey(projectId),
    () => {
      if (!projectId) {
        return Promise.resolve();
      }

      return ProjectAPI.getVariablesData(projectId);
    },
    {
      ...queryOptions,
      enabled: !!projectId,
      refetchOnMount: true,
    },
  );

// TODO: to have separate hook for project and template.
export const useVariablesMentionData = ({
  projectId,
  templateType,
}: {
  projectId?: number;
  templateType?: TemplateObjectType;
}) => {
  const { data: projectFields } = useEnabledProjectFieldsQuery();
  const calculatedProjectFieldOptions = useCalculatedProjectFields();
  const realTimeVariablesResponse = useProjectVariablesQuery(projectId);
  const { data: teamMemberFields } = useTeamMemberFieldsQuery();
  const nativeMemberFieldsOptions = useNativeMemberFieldsOptions();

  // this fetches account level placeholders and project level placeholders if projectId is passed.
  const _placeholders = useGetAllTeamsFromIndexedDbConditionally(projectId);

  const projectTemplatePlaceholders = useSelector(
    getActiveTemplatePlaceholders(),
  );

  const targetPlaceholders = useMemo(() => {
    if (templateType === TemplateObjectType.Project) {
      return [..._placeholders, ...Object.values(projectTemplatePlaceholders)];
    }

    return _placeholders;
  }, [_placeholders, projectTemplatePlaceholders, templateType]);

  const nativeMemberFieldsOptionsForMultiUserField = useMemo(
    () =>
      nativeMemberFieldsOptions.filter((_option) =>
        nativeMemberFieldsForMultiUser.includes(_option.id),
      ),
    [nativeMemberFieldsOptions],
  );

  const realTimeData = useMemo(
    () =>
      realTimeVariablesResponse ? realTimeVariablesResponse.data?.project : {},
    [realTimeVariablesResponse],
  );

  const getRealTimeValue = useCallback(
    (_field) => {
      if (_field.fieldDataType === 'USER') {
        return '';
      }
      const _value = _field.customField
        ? realTimeData?.customField?.[_field.fieldId]
        : _field.fieldName === 'annualizedRecurringRevenue'
        ? realTimeData?.arr
        : realTimeData?.[_field.fieldName];

      return (_field.fieldDataType === FieldType.Boolean.toUpperCase() &&
        _value !== undefined) ||
        _value === 0
        ? `${_value}`
        : _value;
    },
    [realTimeData],
  );

  const getRealTimeValueForNestedField = useCallback(
    (parent, nestedFieldName) => {
      const _parent = parent.customField
        ? realTimeData?.customField?.[parent.fieldId]
        : parent.fieldName === 'projectOwner'
        ? realTimeData?.owner
        : realTimeData?.[parent.fieldName];

      const _value =
        nestedFieldName === 'role'
          ? _parent?.role?.roleName
          : _parent?.[nestedFieldName];

      return parent.fieldDataType === FieldType.Boolean.toUpperCase() &&
        _value !== undefined
        ? `${_value}`
        : _value;
    },
    [realTimeData],
  );

  const getRealTimeValueForPlaceholderField = useCallback(
    (_team, nestedFieldName) => {
      const _parent = realTimeData?.placeholder?.[_team.teamId];

      return nestedFieldName === 'role'
        ? _parent?.role?.roleName
        : _parent?.[nestedFieldName];
    },
    [realTimeData],
  );

  const getUserFieldOptions = useCallback(
    (_field, index, parentFEValue, parentBEValue) => {
      const isUserDataType =
        _field.fieldDataType === 'USER' ||
        /**
         * since BE returns project owner as number field,
         * whereas it should be treated as user data type.
         */
        _field.fieldName === 'projectOwner';

      const isMultiSelectUserField =
        isUserDataType && _field.fieldType === 'MULTI_SELECT';

      return isUserDataType
        ? {
            nestedOptions: [
              ...(isMultiSelectUserField
                ? nativeMemberFieldsOptionsForMultiUserField
                : nativeMemberFieldsOptions
              ).map((_option, _index) => ({
                id: `${_field.fieldId}_${_option.id}`,
                indexString: `${index}.${_index}`,
                label: _option.label,
                value: `${parentFEValue}.${_option.value}`,
                backendValue:
                  _option.value === 'role'
                    ? `${parentBEValue}.role.roleName`
                    : `${parentBEValue}.${_option.value}`,
                realTimeValue: getRealTimeValueForNestedField(
                  _field,
                  _option.value,
                ),
              })),
              ...(isMultiSelectUserField ? [] : teamMemberFields ?? [])?.map(
                (_option, _index) => {
                  const frontendValue = `${parentFEValue}.${_option.fieldName}`;
                  const backendValue = `${parentBEValue}.${_option.fieldName}`;

                  return {
                    id: `${_field.fieldId}_${_option.fieldId}`,
                    indexString: `${index}.${_index}`,
                    label: _option.fieldLabel,
                    value: frontendValue,
                    backendValue,
                    realTimeValue: getRealTimeValueForNestedField(
                      _field,
                      _option.fieldName,
                    ),
                    /**
                     * below code is to support multiple nesting of
                     * team member fields.
                     */
                    // ...getUserFieldOptions(
                    //   _option,
                    //   _index,
                    //   frontendValue,
                    //   backendValue,
                    // ),
                  };
                },
              ),
            ],
          }
        : {};
    },
    [
      getRealTimeValueForNestedField,
      nativeMemberFieldsOptions,
      nativeMemberFieldsOptionsForMultiUserField,
      teamMemberFields,
    ],
  );

  const fieldMapper = useCallback(
    (_field, index) => {
      const value = `project.${
        _field.fieldName === 'annualizedRecurringRevenue'
          ? 'arr'
          : _field.fieldName === 'projectOwner'
          ? 'owner'
          : _field.fieldName === 'Status'
          ? 'status'
          : _field.fieldName
      }`;

      const backendValue =
        _field.customField && _field.fieldName !== 'Status'
          ? `project.customField.${_field.fieldId}`
          : value;

      const frontendValue = `project.${
        _field.fieldName === 'annualizedRecurringRevenue'
          ? 'arr'
          : _field.fieldName === 'projectOwner'
          ? 'owner'
          : _field.fieldName === 'Status'
          ? 'status'
          : _field.fieldLabel
      }`;

      return {
        id: _field.fieldId,
        indexString: `${index}`,
        label: _field.fieldLabel,
        value: frontendValue,
        backendValue,
        fieldDataType: _field.fieldDataType,
        fieldType: _field.fieldType,
        fieldName: _field.fieldName,
        realTimeValue: getRealTimeValue(_field),
        ...getUserFieldOptions(_field, index, frontendValue, backendValue),
      };
    },
    [getRealTimeValue, getUserFieldOptions],
  );

  const projectFieldsOptions = useMemo(() => {
    return projectFields
      ?.filter(
        ({ fieldName, customField }) =>
          customField ||
          [
            'projectId',
            'projectName',
            'projectOwner',
            'dueDate',
            'startDate',
            'projectFee',
            'annualizedRecurringRevenue',
            'budgetedHours',
          ].includes(fieldName),
      )
      ?.concat(calculatedProjectFieldOptions as any)
      ?.map(fieldMapper);
  }, [calculatedProjectFieldOptions, fieldMapper, projectFields]);

  const fieldOptionsWithPlaceholders = projectFieldsOptions?.concat(
    targetPlaceholders.map((_placeholder, index) => {
      const value = `project.${_placeholder.teamName}`;
      const backendValue = `project.placeholder.${_placeholder.teamId}`;
      const actualIndex = projectFieldsOptions.length + index;

      return {
        id: _placeholder.teamId,
        indexString: `${actualIndex}`,
        label: _placeholder.teamName,
        value,
        backendValue,
        fieldDataType: FieldDataType.User,
        fieldType: '',
        fieldName: _placeholder.teamName,
        realTimeValue: null,
        nestedOptions: [
          ...nativeMemberFieldsOptions.map((_option, _index) => ({
            id: `${_placeholder.teamId}_${_option.id}`,
            indexString: `${actualIndex}.${_index}`,
            label: _option.label,
            value: `${value}.${_option.value}`,
            backendValue:
              _option.value === 'role'
                ? `${backendValue}.role.roleName`
                : `${backendValue}.${_option.value}`,
            realTimeValue: getRealTimeValueForPlaceholderField(
              _placeholder,
              _option.value,
            ),
          })),
          ...(teamMemberFields ?? [])?.map((_option, _index) => ({
            id: `${_placeholder.teamId}_${_option.fieldId}`,
            indexString: `${actualIndex}.${_index}`,
            label: _option.fieldLabel,
            value: `${value}.${_option.fieldName}`,
            backendValue: `${backendValue}.${_option.fieldName}`,
            realTimeValue: getRealTimeValueForPlaceholderField(
              _placeholder,
              _option.fieldName,
            ),
          })),
        ],
      };
    }),
  );

  /**
   * sorts the options by fields with values at the top
   * and fields without values at the end of the list.
   */
  if (projectId && !realTimeVariablesResponse.isLoading) {
    return [
      ...(fieldOptionsWithPlaceholders?.filter(
        (_option) => _option.realTimeValue !== undefined,
      ) ?? []),
      ...(fieldOptionsWithPlaceholders?.filter(
        (_option) => _option.realTimeValue === undefined,
      ) ?? []),
    ].map((_option, index) => ({
      ..._option,
      indexString: `${index}`,
    }));
  }

  return fieldOptionsWithPlaceholders;
};

export const useProjectFieldsForVariables = (): {
  loading: boolean;
  fields: FilterableField[];
} => {
  const { loading: customFieldsLoading, fields: customFields } =
    useProjectCustomFieldsAsFilterableFields();
  const { loading: nativeFieldsLoading, fields: nativeFields } =
    useProjectNativeFieldsForVariables();

  return useMemo(
    () => ({
      loading: customFieldsLoading || nativeFieldsLoading,
      fields: nativeFields.concat(
        customFields.filter((field) => field?.isEnabled),
      ),
    }),
    [customFields, customFieldsLoading, nativeFields, nativeFieldsLoading],
  );
};
